import { faSidebarFlip } from "@fortawesome/pro-light-svg-icons";
import { faFile, faFolder, faPalette } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import ReactPlayer from "react-player";

import BgGrid from "@/assets/bg_grid.png";
import { Icon } from "@/components/ui-v2";
import { fileIsModel } from "@/utils/libraryObjectUtils";
import { isAudioMimetype, isVideoMimetype } from "@/utils/mimetype";
import { hexToRgba } from "@/utils/styling";

import { FileImageErrored } from "../file-image-errored/FileImageErrored";
import { fileIcon } from "../files/icons";
import { AudioIcon } from "../ui-v2/icons";
import { SelectedObject } from "./types";

const MAX_NUM_PREVIEW_IMAGES = 3;

const StyledPreviewImage = styled("img")({
  position: "absolute",
  width: "91.666667%",
  height: "100%",
  objectFit: "contain",
  transition: "transform 500ms ease-in-out",
});

const StyledPreviewIcon = styled(Box)(({ theme }) => ({
  position: "absolute",
  width: "60%",
  height: "60%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.secondary[7],
  borderRadius: "8px",
  transition: "transform 500ms ease-in-out",
}));

type ObjectPreview = {
  name: string;
  videoUrl?: string;
  imageUrl?: string;
  icon?: JSX.Element;
};

type PreviewImageProps = {
  object: ObjectPreview;
  index: number;
  numTotalImages: number;
};

const PreviewImage = (props: PreviewImageProps) => {
  const { object, index, numTotalImages } = props;
  const theme = useTheme();
  const [isImageError, setIsImageError] = useState(false);

  const handleImageError = () => {
    setIsImageError(true);
  };

  useEffect(() => {
    setIsImageError(false);
  }, [object.imageUrl]);

  if (object.videoUrl) {
    return <ReactPlayer url={object.videoUrl} />;
  }

  if (object.imageUrl) {
    if (isImageError) {
      return (
        <FileImageErrored
          dismissable={false}
          errorMessage="Preview image could not be loaded."
        />
      );
    }
    return (
      <StyledPreviewImage
        src={object.imageUrl}
        onError={handleImageError}
        style={{
          transform: `rotate(${(numTotalImages - 1 - index) * 10}deg)`,
        }}
        alt={`Preview ${index + 1}`}
      />
    );
  }

  if (object.icon) {
    return (
      <StyledPreviewIcon
        style={{
          transform: `rotate(${(numTotalImages - 1 - index) * 10}deg)`,
        }}
      >
        <Typography sx={{ color: theme.palette.success.main }} variant="title1">
          {object.icon}
        </Typography>
      </StyledPreviewIcon>
    );
  }

  return null;
};

const PreviewContainer = styled("div")(({ theme }) => ({
  position: "relative",
  zIndex: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  aspectRatio: "1/1",
  border: `1px solid ${theme.palette.secondary[6]}`,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
}));

const HoverOverlay = styled("div")(({ theme }) => ({
  position: "absolute",
  zIndex: 20,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  gap: theme.spacing(2),
  pointerEvents: "none",
  backgroundColor: hexToRgba(theme.palette.secondary[8], 0.8),
  color: theme.palette.secondary[2],
}));

const PreviewWrapper = styled("div")({
  position: "absolute",
  zIndex: 10,
  display: "block",
  width: "100%",
  height: "100%",
  padding: "0.25rem",
});

const ImageContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  borderRadius: "0.125rem",
  overflow: "hidden",
});

const GridBackground = styled("div")({
  width: "100%",
  height: "100%",
  backgroundPosition: "top",
  backgroundRepeat: "repeat",
  backgroundSize: "31.5px 31.5px",
  opacity: 0.75,
});

type PreviewContentProps = {
  objects: SelectedObject[];
  isGalleryOpen?: boolean;
  isShowingInPreview?: boolean;
  onClickPreview?: (isGalleryOpen: boolean) => void;
};

export const PreviewContent = ({
  objects,
  isGalleryOpen,
  isShowingInPreview,
  onClickPreview,
}: PreviewContentProps) => {
  const [isPreviewHovered, setIsPreviewHovered] = useState(false);

  if (!objects.length) {
    return null;
  }

  const isSelectionPreviewable = objects.some(
    (obj) => obj.__typename === "File" || obj.__typename === "Asset",
  );

  const objectPreviews = objects
    .slice(0, MAX_NUM_PREVIEW_IMAGES)
    .map(mapObjectToPreviewObject);

  const handlePreviewOpen = () => {
    if (!isSelectionPreviewable) {
      return;
    }

    onClickPreview?.(!isGalleryOpen);
  };

  const handlePreviewMouseOver = () => {
    setIsPreviewHovered(true);
  };

  const handlePreviewMouseLeave = () => {
    setIsPreviewHovered(false);
  };

  const previewText = isGalleryOpen ? "Showing in Viewer" : "Open in Viewer";

  const overlayedPreviewContent = (
    <HoverOverlay>
      <Icon size="large" icon={faSidebarFlip} />
      <Typography sx={{ fontWeight: 500 }} variant="small">
        {previewText}
      </Typography>
    </HoverOverlay>
  );

  const showOverlay =
    (isSelectionPreviewable && isPreviewHovered) ||
    isGalleryOpen ||
    isShowingInPreview;

  return (
    <Box
      onClick={handlePreviewOpen}
      onMouseOver={handlePreviewMouseOver}
      onMouseLeave={handlePreviewMouseLeave}
      sx={{
        cursor: !isGalleryOpen && !isPreviewHovered ? "pointer" : "default",
      }}
    >
      <PreviewContainer>
        {showOverlay && overlayedPreviewContent}

        <PreviewWrapper>
          <ImageContainer>
            {objectPreviews.toReversed().map((objPreview, index) => {
              return (
                <PreviewImage
                  key={`preview-${index}`}
                  object={objPreview}
                  index={index}
                  numTotalImages={objectPreviews.length}
                />
              );
            })}
          </ImageContainer>
        </PreviewWrapper>

        <GridBackground
          style={{
            backgroundImage: `url(${BgGrid})`,
          }}
        />
      </PreviewContainer>
    </Box>
  );
};

const mapObjectToPreviewObject = (obj: SelectedObject): ObjectPreview => {
  switch (obj.__typename) {
    case "Asset":
      return {
        name: obj.name,
        imageUrl: obj.previewUrl ?? undefined,
      };
    case "File":
      if (obj.contentType) {
        if (isVideoMimetype(obj.contentType)) {
          return {
            name: obj.name,
            videoUrl: obj.url,
          };
        }

        if (fileIsModel(obj)) {
          return {
            name: obj.name,
            icon: fileIcon(obj),
          };
        }

        if (isAudioMimetype(obj.contentType)) {
          return {
            name: obj.name,
            icon: <AudioIcon />,
          };
        }
      }

      return {
        name: obj.name,
        imageUrl: obj.thumbnails?.medium?.url ?? obj.previewUrl ?? undefined,
      };
    case "Directory":
      return {
        name: obj.name,
        icon: <FontAwesomeIcon icon={faFolder} />,
      };
    case "Style":
    case "ReferenceSet": {
      return {
        name: obj.name,
        icon: <Icon icon={faPalette} />,
      };
    }
    default:
      return {
        name: obj["name"] ?? "Item",
        icon: <FontAwesomeIcon icon={faFile} />,
      };
  }
};
